import {uniqBy} from 'es-toolkit'
import {get, isEmpty} from 'es-toolkit/compat'
import {FormikProps, getIn} from 'formik'
import * as Yup from 'yup'

import config from '../../../config'

import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import {PossiblyMixedValue, isMixedValue, unlockMixedValuesLabel} from '../../utils/form'

import closeIcon from '../../../../../commons/src/assets/icons/close.svg'
import plusIcon from '../../../../../commons/src/assets/icons/plus.svg'

import Button from '../../../../../commons/src/components/button'
import FieldLabel from '../../../../../commons/src/components/field-label/FieldLabel'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import Icon from '../../../../../commons/src/components/icon'
import InputField from '../../../../../commons/src/components/input-field'
import LinkButton from '../../../../../commons/src/components/link-button'
import NumberInputField from '../../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../../commons/src/components/paragraph'
import Repeatable from '../../../../../commons/src/components/repeatable'
import MixedValueFormSection from '../../../components/mixed-value-form-section'

export type QuantityThreshold = {
  axisSize: string
  qtyThreshold: string
}

export type SingleFormData = {
  name: string
  increaseSteps: string
  maxIncrease: string
  quantityThresholds: QuantityThreshold[]
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export type MixedFormData = {
  name: PossiblyMixedValue<string>
  increaseSteps: PossiblyMixedValue<string>
  maxIncrease: PossiblyMixedValue<string>
  quantityThresholds: PossiblyMixedValue<QuantityThreshold[]>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export const initialValues: SingleFormData = {
  name: '',
  increaseSteps: '',
  maxIncrease: '',
  quantityThresholds: [{axisSize: '0', qtyThreshold: ''}],
}

export const validationSchema = {
  name: Yup.string().label('Name').required(),
  increaseSteps: Yup.number()
    .min(1)
    .max(config.dynamicProductionTimes.maxIncreaseSteps)
    .label('Increase steps')
    .required(),
  maxIncrease: Yup.number()
    .min(1)
    .max(config.dynamicProductionTimes.maxIncrease)
    .label('Max increase')
    .required(),
  quantityThresholds: Yup.array()
    .of(
      Yup.object({
        axisSize: Yup.number().label('Axis size').required(),
        qtyThreshold: Yup.number().label('Quantity threshold').required(),
      })
    )
    .test('UniquAxisSizes', 'Axis sizes must be different', (quantityThresholds, ctx) => {
      if (quantityThresholds) {
        const uniqueData = uniqBy(quantityThresholds, item => item.axisSize)

        const isUnique = quantityThresholds.length === uniqueData.length

        if (isUnique) {
          return true
        }
        const nonUniqueIndex = quantityThresholds.findIndex(
          (row, i) => (!!row && row.axisSize) !== (uniqueData[i] ? uniqueData[i].axisSize : '')
        )

        return ctx.createError({
          path: `${ctx.path}.${nonUniqueIndex}.${'axisSize'}`,
        })
      }
      return true
    })
    .test(
      'QtyThresholdGTZero ',
      'Quantity threshold must be greater than 0',
      (quantityThresholds, ctx) => {
        if (quantityThresholds) {
          const invalidQtyThresholds = quantityThresholds.filter(row => row.qtyThreshold <= 0)
          if (isEmpty(invalidQtyThresholds)) {
            return true
          }
          const invalidQtyThresholdIndex = quantityThresholds.findIndex(
            row => row.qtyThreshold <= 0
          )
          return ctx.createError({
            path: `${ctx.path}.${invalidQtyThresholdIndex}.${'qtyThreshold'}`,
          })
        }
        return true
      }
    )
    .required(),
}

type Props = {
  formik: FormikProps<SingleFormData> | FormikProps<MixedFormData>
}

const DynamicProductionTimeForm = ({formik}: Props) => {
  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  const getInitialQuantityThreshold = (index: number) => ({
    axisSize: index === 0 ? '0' : '',
    qtyThreshold: '',
  })

  const setPossiblyMixedQuantityThresholds = () => {
    formik.setFieldValue('quantityThresholds', [getInitialQuantityThreshold(0)])
  }

  const hasError = (key: string) => formik.submitCount > 0 && !!getIn(formik.errors, key)

  const renderQuantityThresholdRow = (item: QuantityThreshold, index: number) => (
    <FormRow key={index} layout="half-half">
      {/* AXIS SIZE */}
      <FieldNote
        appearance="error"
        note={formik.submitCount > 0 && get(formik.errors, `quantityThresholds[${index}].axisSize`)}
      >
        <NumberInputField
          allowDecimals
          placeholder="Axis size"
          name={`quantityThresholds[${index}].axisSize`}
          value={item.axisSize}
          suffix="mm"
          disabled={index === 0}
          appearance={hasError(`quantityThresholds[${index}].axisSize`) ? 'error' : undefined}
          onChange={(value, name) => formik.setFieldValue(name as string, value)}
          onBlur={formik.handleBlur}
        />
      </FieldNote>
      {/* QUANTITY THRESHOLD */}
      <FieldNote
        appearance="error"
        note={
          formik.submitCount > 0 && get(formik.errors, `quantityThresholds[${index}].qtyThreshold`)
        }
      >
        <NumberInputField
          allowDecimals
          placeholder="Quantity threshold"
          name={`quantityThresholds[${index}].qtyThreshold`}
          value={item.qtyThreshold}
          appearance={hasError(`quantityThresholds[${index}].qtyThreshold`) ? 'error' : undefined}
          onChange={(value, name) => formik.setFieldValue(name as string, value)}
          onBlur={formik.handleBlur}
        />
      </FieldNote>
    </FormRow>
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      {/* NAME */}
      <FormRow>
        <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.name}>
          <FieldLabel label="Name">
            <InputField
              placeholder="for your internal reference only"
              name="name"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.name)}
              value={isMixedValue(formik.values.name) ? initialValues.name : formik.values.name}
              appearance={hasError('name') ? 'error' : undefined}
              onChange={(value, name, event) => formik.handleChange(event)}
              onBlur={formik.handleBlur}
              action={
                isMixedValue(formik.values.name) ? (
                  <Button
                    hierarchy="ghost-input-action"
                    icon={<Icon block source={closeIcon} title={unlockMixedValuesLabel} />}
                    onClick={() => formik.setFieldValue('name', initialValues.name)}
                  />
                ) : undefined
              }
            />
          </FieldLabel>
        </FieldNote>
      </FormRow>
      <Headline label="Increase steps" className="u-margin-top-l" />
      {/* INCREASE STEPS */}
      <FormRow layout="half-half">
        <FieldLabel label="Increase by">
          <FieldNote
            appearance="error"
            note={formik.submitCount > 0 && formik.errors.increaseSteps}
          >
            <NumberInputField
              allowDecimals
              name="increaseSteps"
              mixedLabel="Mixed"
              suffix="days"
              mixed={isMixedValue(formik.values.increaseSteps)}
              value={
                isMixedValue(formik.values.increaseSteps)
                  ? initialValues.increaseSteps
                  : formik.values.increaseSteps
              }
              appearance={hasError('increaseSteps') ? 'error' : undefined}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
              action={
                isMixedValue(formik.values.increaseSteps) ? (
                  <Button
                    hierarchy="ghost-input-action"
                    icon={<Icon block source={closeIcon} title={unlockMixedValuesLabel} />}
                    onClick={() =>
                      formik.setFieldValue('increaseSteps', initialValues.increaseSteps)
                    }
                  />
                ) : undefined
              }
            />
          </FieldNote>
        </FieldLabel>
        {/* MAX INCREASE */}
        <FieldLabel label="Max increase">
          <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.maxIncrease}>
            <NumberInputField
              allowDecimals
              name="maxIncrease"
              mixedLabel="Mixed"
              suffix="days"
              mixed={isMixedValue(formik.values.maxIncrease)}
              value={
                isMixedValue(formik.values.maxIncrease)
                  ? initialValues.maxIncrease
                  : formik.values.maxIncrease
              }
              appearance={hasError('maxIncrease') ? 'error' : undefined}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
              action={
                isMixedValue(formik.values.maxIncrease) ? (
                  <Button
                    hierarchy="ghost-input-action"
                    icon={<Icon block source={closeIcon} title={unlockMixedValuesLabel} />}
                    onClick={() => formik.setFieldValue('maxIncrease', initialValues.maxIncrease)}
                  />
                ) : undefined
              }
            />
          </FieldNote>
        </FieldLabel>
      </FormRow>
      {/* QUANTITY THRESHOLDS */}
      <Headline label="Quantity thresholds" className="u-margin-top-l" />
      <MixedValueFormSection
        locked={isMixedValue(formik.values.quantityThresholds)}
        onClick={setPossiblyMixedQuantityThresholds}
        unlockButtonLabel={unlockMixedValuesLabel}
      >
        <FormRow>
          <Repeatable
            preventDeleteMin
            min={1}
            button={<LinkButton icon={<Icon source={plusIcon} />}>Add threshold</LinkButton>}
            onAdd={nextIndex => {
              formik.setFieldValue('quantityThresholds', [
                ...(formik.values.quantityThresholds as QuantityThreshold[]),
                getInitialQuantityThreshold(nextIndex),
              ])
            }}
            onDelete={index => {
              formik.setFieldValue(
                'quantityThresholds',
                (formik.values.quantityThresholds as QuantityThreshold[]).filter(
                  (_, i) => i !== index
                )
              )
            }}
          >
            {!isMixedValue(formik.values.quantityThresholds) &&
              (formik.values.quantityThresholds as QuantityThreshold[]).map((item, index) =>
                renderQuantityThresholdRow(item, index)
              )}
            {isMixedValue(formik.values.quantityThresholds) &&
              [getInitialQuantityThreshold(0)].map((item, index) =>
                renderQuantityThresholdRow(item, index)
              )}
          </Repeatable>
        </FormRow>
      </MixedValueFormSection>
    </form>
  )
}
export default DynamicProductionTimeForm
